import * as React from 'react'
import { observer, inject } from 'mobx-react'
import { AuditProductAPI, AuditBatchAPI } from '@getgreenline/homi-shared'
import { AuditBatchTable } from '../AuditBatchTable'
import { BatchStatusToggleTd } from '../../../../../../../components/BatchStatusToggleTd'
import { AuditDiscrepancy } from './AuditDiscrepancy'
import { AuditCostDiscrepancy } from './AuditCostDiscrepancy'
import { message, Button, Tooltip, Typography } from 'antd-v4'
import { parseErrorMsg } from '../../../../../../../utilities/helpers'
import Highlighter from 'react-highlight-words'
import { CurrentCompanyStore } from '../../../../../../../stores/CurrentCompanyStore'
import { AuditStore } from '../../EditAuditStore'
import { AuditProductEnteredQuantityInput } from './AuditProductEnteredQuantityInput'
import { AuditProductNotesInput } from './AuditProductNotesInput'
import { AuditModels, AuditProductModels } from '@getgreenline/audits'
import { ExclamationCircleTwoTone, CloseOutlined } from '@ant-design/icons'

interface Props {
  product: AuditProductModels.EditAuditProduct
  audit: AuditModels.CreateInventoryAudit
  isAuditEditable: boolean
  containsBatchProduct: boolean
  auditStore: AuditStore
  searchText?: string
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  isBatchClicked: boolean
  isDeletingAuditProduct: boolean
}

enum BatchQuantityKeys {
  EXPECTED_QUANTITY = 'expectedQuantity',
  ENTERED_QUANTITY = 'enteredQuantity',
}

@inject('currentCompanyStore')
@observer
export class EditAuditProductRow extends React.Component<Props & InjectedProps, State> {
  companyId = this.props.currentCompanyStore!.company!.id

  constructor(props: Props) {
    super(props)

    this.state = {
      isBatchClicked: false,
      isDeletingAuditProduct: false,
    }
  }

  toggleIsBatchClicked = () => this.setState({ isBatchClicked: !this.state.isBatchClicked })

  getBatchQuantitySum = (sumEntity: BatchQuantityKeys) => {
    const { batches: allBatches } = this.props.product

    return allBatches.reduce((pre, next) => {
      const nextValue = next[sumEntity] || 0
      return pre + nextValue
    }, 0)
  }

  deleteProduct = async () => {
    const { audit, product } = this.props

    this.setState({ isDeletingAuditProduct: true })

    try {
      // This should be replaced with removing all batches per product
      for (const auditBatch of product.batches) {
        await AuditBatchAPI.deleteAuditBatch(this.companyId, audit.id, auditBatch.id)
      }

      await AuditProductAPI.deleteAuditProduct(this.companyId, audit.id, product.id)

      audit.removeAuditProduct(product.productId)

      message.success(
        <>
          Product <strong>{product.productName}</strong> was successfully removed
        </>,
      )
    } catch (error) {
      message.error(
        parseErrorMsg(error) || `An error occurred while deleting ${product.productName}`,
      )
    } finally {
      this.setState({ isDeletingAuditProduct: false })
    }
  }

  get splitSearchWords() {
    return this.props.searchText ? [...this.props.searchText.split(' ')] : []
  }

  get expectedQuantity(): string | number {
    const { product } = this.props

    if (product.enteredQuantity === null || product.expectedQuantity === null) {
      return '-'
    }

    return product.expectedQuantity
  }

  render() {
    const { isBatchClicked, isDeletingAuditProduct } = this.state
    const { product, audit, auditStore, isAuditEditable, containsBatchProduct } = this.props

    const totalBatchCountedQuantity = this.getBatchQuantitySum(BatchQuantityKeys.ENTERED_QUANTITY)

    return (
      <>
        <tr id={`id-${product.productId}`}>
          <td style={{ minWidth: 200, maxWidth: 250 }}>
            <Highlighter
              highlightClassName='highlighted-text'
              searchWords={this.splitSearchWords}
              autoEscape
              textToHighlight={product.productName}
            />

            <div className='text-muted'>
              {product.sku ? (
                <Highlighter
                  highlightClassName='highlighted-text'
                  searchWords={this.splitSearchWords}
                  autoEscape
                  textToHighlight={product.sku}
                />
              ) : (
                '-'
              )}
            </div>
          </td>

          {containsBatchProduct &&
            (product.isBatchTracked ? (
              <BatchStatusToggleTd
                isBatchClicked={isBatchClicked}
                toggleIsBatchClicked={this.toggleIsBatchClicked}
                totalCountedQuantity={totalBatchCountedQuantity}
                totalExpectedQuantity={product.enteredQuantity || 0}
              />
            ) : (
              <td className='text-center'>-</td>
            ))}

          <td className='text-right' style={{ minWidth: '7rem', width: '7rem' }}>
            {isAuditEditable ? (
              <AuditProductEnteredQuantityInput product={product} auditStore={auditStore} />
            ) : (
              <div>{product.enteredQuantity === null ? '-' : product.enteredQuantity}</div>
            )}
          </td>

          {auditStore.shouldDisplayQuantity && (
            <>
              <td className='text-right'>
                <div className='d-flex justify-content-end'>
                  {product.expectedQuantity === null && (
                    <Tooltip
                      title={
                        <div>
                          <div>
                            <p>
                              <span>The expected quantity may be inaccurate.</span>
                            </p>

                            <p>
                              <span>This is because the product may have been </span>
                              <Typography.Text underline style={{ color: 'white' }}>
                                created
                              </Typography.Text>
                              <span>, </span>
                              <Typography.Text underline style={{ color: 'white' }}>
                                activated
                              </Typography.Text>
                              <span>, </span>
                              <Typography.Text underline style={{ color: 'white' }}>
                                undeleted
                              </Typography.Text>
                              <span>, or </span>
                              <Typography.Text underline style={{ color: 'white' }}>
                                inventory tracked
                              </Typography.Text>
                              <span> </span>
                              <span>
                                <b>while</b> the audit is in progress.
                              </span>
                            </p>
                            <p>Or inventory is 0 and has not been updated in over a month.</p>
                          </div>

                          <p className='mb-0'>
                            <span>Please complete a new audit for these products.</span>
                          </p>
                        </div>
                      }
                    >
                      <ExclamationCircleTwoTone
                        twoToneColor='orange'
                        className='mr-1'
                        style={{ alignSelf: 'center' }}
                      />
                    </Tooltip>
                  )}
                  {this.expectedQuantity}
                </div>
              </td>

              <td className='text-right'>
                <AuditDiscrepancy
                  enteredQuantity={product.locallySavedEnteredQuantity}
                  expectedQuantity={product.expectedQuantity}
                />
              </td>

              <td className='text-right'>
                <AuditCostDiscrepancy
                  enteredQuantity={product.locallySavedEnteredQuantity}
                  expectedQuantity={product.expectedQuantity}
                  purchasePrice={product.purchasePrice}
                />
              </td>
            </>
          )}

          <td className='text-right' style={{ minWidth: 200 }}>
            <AuditProductNotesInput product={product} auditStore={auditStore} />
          </td>

          {isAuditEditable && (
            <td className='text-center' style={{ width: 68 }}>
              <Button
                icon={<CloseOutlined />}
                size='small'
                loading={isDeletingAuditProduct}
                onClick={this.deleteProduct}
                tabIndex={-1}
              />
            </td>
          )}
        </tr>

        {isBatchClicked && (
          <tr style={{ backgroundColor: '#fbfbfb' }}>
            <td colSpan={9}>
              <AuditBatchTable
                auditStore={auditStore}
                auditProduct={product}
                isAuditEditable={isAuditEditable}
                audit={audit}
              />
            </td>
          </tr>
        )}
      </>
    )
  }
}
